import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { SectionSplitProps } from '../../utils/SectionProps';
import SectionHeader from './partials/SectionHeader';
import { useMediaQuery } from 'react-responsive';
import ButtonGroup from '../elements/ButtonGroup';
import { Link } from 'react-router-dom';
import { redirectToMobileStore } from '../../../../util/mobileStoreRedirect';
import captureInboxIllustration from '../../assets/images/feature-capture-inbox.webp';
import captureInboxIllustrationSmall from '../../assets/images/feature-capture-inbox.small.webp';
import searchMemoryIllustration from '../../assets/images/feature-search-memory.webp';
import searchMemoryIllustrationSmall from '../../assets/images/feature-search-memory.small.webp';
import organizeUsefulIllustration from '../../assets/images/feature-organize-useful.webp';
import organizeUsefulIllustrationSmall from '../../assets/images/feature-organize-useful.small.webp';

const FeatureIllustration = ({ src, smallSrc, alt }) => (
    <img
        src={src}
        srcSet={`${smallSrc} 724w, ${src} 1448w`}
        sizes="(max-width: 767px) 92vw, 560px"
        alt={alt}
        style={{
            width: 'min(560px, 100%)',
            height: 'auto',
            borderRadius: '18px',
            display: 'block'
        }}
    />
);

FeatureIllustration.propTypes = {
    src: PropTypes.string.isRequired,
    smallSrc: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired
};

const propTypes = {
    ...SectionSplitProps.types
};

const defaultProps = {
    ...SectionSplitProps.defaults
};

const FeaturesSplit = ({
    className,
    topOuterDivider,
    bottomOuterDivider,
    topDivider,
    bottomDivider,
    hasBgColor,
    invertColor,
    invertMobile,
    invertDesktop,
    alignTop,
    imageFill,
    ...props
}) => {
    const outerClasses = classNames(
        'features-split section',
        topOuterDivider && 'has-top-divider',
        bottomOuterDivider && 'has-bottom-divider',
        hasBgColor && 'has-bg-color',
        invertColor && 'invert-color',
        className
    );

    const innerClasses = classNames(
        'features-split-inner section-inner',
        topDivider && 'has-top-divider',
        bottomDivider && 'has-bottom-divider'
    );

    const splitClasses = classNames(
        'split-wrap',
        invertMobile && 'invert-mobile',
        invertDesktop && 'invert-desktop',
        alignTop && 'align-top'
    );

    const sectionHeader = {
        title: 'Your memory, without the maintenance',
        paragraph: 'Luckynote keeps what you save useful and easy to return to—without turning organization into another job.'
    };

    const isMobile = useMediaQuery({ maxWidth: 767 });

    return (
        <section {...props} className={outerClasses}>
            <div className="container">
                <div className={innerClasses}>
                    <SectionHeader data={sectionHeader} className="center-content" />
                    <div className={splitClasses}>
                        <div className="split-item">
                            <div
                                className="split-item-content center-content-mobile reveal-from-left"
                                data-reveal-container=".split-item"
                            >
                                {/* <div className="text-xxs text-color-primary fw-600 tt-u mb-8">
                                    Lightning fast workflow
                                </div> */}
                                <h3 className="mt-0 mb-12">Drop things in before they disappear</h3>
                                <p className="m-0">
                                    <strong>As easy as messaging yourself.</strong> Send a photo, a voice note, a link, a file, or a half-typed thought. Save it the way it
                                    shows up, without stopping to decide where it belongs.
                                    <br />
                                    <br />
                                    Luckynote reads text inside screenshots, transcribes voice notes, and keeps link
                                    context, so quick saves become useful knowledge later.
                                </p>
                            </div>
                            <div
                                className={classNames(
                                    'split-item-image center-content-mobile reveal-from-bottom',
                                    imageFill && 'split-item-image-fill'
                                )}
                                data-reveal-container=".split-item"
                            >
                                <FeatureIllustration
                                    src={captureInboxIllustration}
                                    smallSrc={captureInboxIllustrationSmall}
                                    alt="Luckynote inbox with links, voice notes, screenshots, files, and tasks"
                                />
                            </div>
                        </div>

                        <div className="split-item">
                            <div
                                className="split-item-content center-content-mobile reveal-from-right"
                                data-reveal-container=".split-item"
                            >
                                <h3 className="mt-0 mb-12">Remember what, not where</h3>
                                <p className="m-0">
                                    Search &quot;that pasta recipe from Instagram&quot; and it comes up, even though you never
                                    typed a word of it.
                                    <br />
                                    <br />
                                    Search the detail you remember, even if it came from a screenshot, link, file, or
                                    voice note. That{' '}
                                    <Link
                                        to="/use-cases/recipes"
                                        style={{ fontWeight: 600, color: '#667eea', textDecoration: 'none' }}
                                    >
                                        recipe
                                    </Link>{' '}
                                    can live in a folder later. You do not need one before you save it.
                                </p>
                            </div>
                            <div
                                className={classNames(
                                    'split-item-image center-content-mobile reveal-from-bottom',
                                    imageFill && 'split-item-image-fill'
                                )}
                                data-reveal-container=".split-item"
                            >
                                <FeatureIllustration
                                    src={searchMemoryIllustration}
                                    smallSrc={searchMemoryIllustrationSmall}
                                    alt="Search result found from text inside a saved screenshot"
                                />
                            </div>
                        </div>

                        <div className="split-item">
                            <div
                                className="split-item-content center-content-mobile reveal-from-left"
                                data-reveal-container=".split-item"
                            >
                                <h3 className="mt-0 mb-12">Stay organized without becoming an organizer</h3>
                                <p className="m-0">
                                    Use folders when they help. Star important notes. Turn anything into a task when it
                                    becomes actionable.
                                    <br />
                                    <br />
                                    Or do none of that and just search later. See how people use Luckynote as a{' '}
                                    <Link
                                        to="/use-cases/personal-inbox"
                                        style={{ fontWeight: 600, color: '#667eea', textDecoration: 'none' }}
                                    >
                                        personal inbox
                                    </Link>
                                    .
                                </p>
                            </div>
                            <div
                                className={classNames(
                                    'split-item-image center-content-mobile reveal-from-bottom',
                                    imageFill && 'split-item-image-fill'
                                )}
                                data-reveal-container=".split-item"
                            >
                                <FeatureIllustration
                                    src={organizeUsefulIllustration}
                                    smallSrc={organizeUsefulIllustrationSmall}
                                    alt="Luckynote organization view with inbox, starred items, tasks, reminders, folders, and tags"
                                />
                            </div>
                        </div>

                        <div style={{
                            justifyContent: 'center',
                            display: 'flex',
                            marginTop: isMobile ? 40 : 64,
                            flexDirection: 'column',
                            alignItems: 'center',
                            paddingTop: isMobile ? 40 : 48,
                            borderTop: '1px solid rgba(0, 0, 0, 0.06)',
                            // maxWidth: '32rem',
                            marginLeft: 'auto',
                            marginRight: 'auto'
                        }}>
                            <div className="center-content" style={{ padding: '0 20px' }}>
                                <h3 style={{ marginBottom: '12px' }}>Put your next save to work</h3>
                                <p style={{ margin: '0 0 24px', color: '#6c757d' }}>
                                    Send something to Luckynote, then come back to the useful detail when you need it.
                                </p>
                            </div>
                            <ButtonGroup>
                                <Link
                                    to="/register"
                                    onClick={redirectToMobileStore}
                                    className="button button-primary button-wide-mobile button-md"
                                    style={{ minHeight: 44 }}
                                >
                                    {isMobile ? "Download the app" : "Start free"}
                                </Link>
                            </ButtonGroup>
                        </div>
                    </div>
                </div>
            </div>
        </section >
    );
};

FeaturesSplit.propTypes = propTypes;
FeaturesSplit.defaultProps = defaultProps;

export default FeaturesSplit;
